import {
    Alert, Box, Button, CircularProgress, Divider, FormControlLabel,
    MenuItem, Snackbar, Switch, TextField, Typography
} from '@mui/material';
import { UploadFile } from '@mui/icons-material';
import { useEffect, useMemo, useRef, useState } from 'react';
import ApiServices from '../../services/ApiServices';

// MCU type sets filtered by coordinator version prefix
const V_MCU_TYPES     = ['JN_5168', 'M_GD512'];
const NON_V_MCU_TYPES = ['JN_5148', 'JN_5168', 'M_G64', 'M_G128', 'M_LG'];
const ALL_MCU_TYPES   = ['JN_5148', 'JN_5168', 'M_G64', 'M_G128', 'M_LG', 'M_GD512'];

// MCU types that belong to mesh2 — used for standalone mode where coordinator is unknown
const MESH2_MCU_SET = new Set(['JN_5168', 'M_GD512']);

// Resolve firmware type for a given MCU type:
//   firmwareType = 'mesh1'|'mesh2' when coordinator is known
//   null = standalone → infer from MCU type
const resolveFwType = (mcuType, firmwareType) =>
    firmwareType ?? (MESH2_MCU_SET.has(mcuType) ? 'mesh2' : 'mesh1');

const timeToSeconds = (hhmm) => {
    if (!hhmm) return 0;
    const [h, m] = hhmm.split(':').map(Number);
    return h * 3600 + m * 60;
};

const HOURS   = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

function TimePicker24({ label, value, onChange }) {
    const h = value ? parseInt(value.split(':')[0], 10) : '';
    const m = value ? parseInt(value.split(':')[1], 10) : '';

    const pick = (newH, newM) => {
        if (newH === '') { onChange(''); return; }
        onChange(`${String(newH).padStart(2, '0')}:${String(newM === '' ? 0 : newM).padStart(2, '0')}`);
    };

    return (
        <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                {label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TextField
                    select
                    size="small"
                    sx={{ flex: 1 }}
                    value={h}
                    onChange={e => pick(e.target.value, m)}
                    SelectProps={{ displayEmpty: true, renderValue: v => v === '' ? 'HH' : String(v).padStart(2, '0') }}
                >
                    <MenuItem value=""><em>--</em></MenuItem>
                    {HOURS.map(i => (
                        <MenuItem key={i} value={i}>{String(i).padStart(2, '0')}</MenuItem>
                    ))}
                </TextField>
                <Typography variant="body2">:</Typography>
                <TextField
                    select
                    size="small"
                    sx={{ flex: 1 }}
                    value={m}
                    onChange={e => pick(h, e.target.value)}
                    disabled={h === ''}
                    SelectProps={{ displayEmpty: true, renderValue: v => v === '' ? 'MM' : String(v).padStart(2, '0') }}
                >
                    <MenuItem value=""><em>--</em></MenuItem>
                    {MINUTES.map(i => (
                        <MenuItem key={i} value={i}>{String(i).padStart(2, '0')}</MenuItem>
                    ))}
                </TextField>
            </Box>
            <Typography variant="caption" color="text.secondary">
                {value ? `→ ${timeToSeconds(value)} seconds` : 'Optional'}
            </Typography>
        </Box>
    );
}


const ROW = { display: 'flex', alignItems: 'center', mb: 0.8, gap: 1 };
const LABEL_SX = { width: 90, flexShrink: 0, fontSize: 13 };

/**
 * GatewayOtaForm
 *
 * Props:
 *   gatewayId         – string|null
 *                       Provided when used inside GatewayDetail (gateway already known).
 *                       Null/undefined when used standalone (e.g. from GatewayTable dialog).
 *
 *   coordinatorVersion – string|null|undefined
 *                        Controls which MCU types are shown:
 *                          starts with 'v'  → JN_5168, M_GD512
 *                          anything else    → JN_5148, JN_5168, M_G64, M_G128, M_LG
 *                          null/undefined   → all 6 types
 *
 * File format when "Update All Devices" is OFF:
 *   Standalone (no gatewayId):  line 1 = gateway MAC (hex), lines 2+ = device MACs (decimal)
 *   Detail (gatewayId known):   lines 1+ = device MACs (decimal) only
 */
function GatewayOtaForm({ gatewayId, coordinatorVersion }) {
    const standalone = !gatewayId;

    // Which MCU types to show based on coordinator version
    const activeMcuTypes = useMemo(() => {
        if (!coordinatorVersion) return ALL_MCU_TYPES;
        return coordinatorVersion.startsWith('v') ? V_MCU_TYPES : NON_V_MCU_TYPES;
    }, [coordinatorVersion]);

    // Firmware type to fetch: x.y coordinator → mesh1, x.y.z → mesh2, unknown → null (per-MCU)
    const firmwareType = useMemo(() => {
        if (!coordinatorVersion) return null;
        return /^\d+\.\d+$/.test(coordinatorVersion) ? 'mesh1' : 'mesh2';
    }, [coordinatorVersion]);

    // x.y.z coordinators don't support scheduled start/stop times
    const hideTimings = firmwareType === 'mesh2';

    const [firmwareOptions, setFirmwareOptions] = useState({});
    const [loadingFirmware, setLoadingFirmware] = useState(true);
    const [selected, setSelected] = useState(
        ALL_MCU_TYPES.reduce((acc, t) => ({ ...acc, [t]: '' }), {})
    );

    // Reset selections that are no longer visible when coordinator version resolves
    useEffect(() => {
        setSelected(prev => {
            const next = activeMcuTypes.reduce((acc, t) => ({ ...acc, [t]: '' }), {});
            // preserve any selections that are still in active set
            activeMcuTypes.forEach(t => { if (prev[t]) next[t] = prev[t]; });
            return next;
        });
    }, [activeMcuTypes]);


    const [startTime, setStartTime] = useState('');
    const [stopTime, setStopTime] = useState('');

    useEffect(() => {
        if (hideTimings) {
            setStartTime('');
            setStopTime('');
        }
    }, [hideTimings]);

    // Device targeting
    const [updateAll, setUpdateAll] = useState(true);
    const [parsedGatewayMac, setParsedGatewayMac] = useState('');
    const [deviceMacs, setDeviceMacs] = useState([]);
    const [fileName, setFileName] = useState('');
    const fileInputRef = useRef(null);

    const [submitting, setSubmitting] = useState(false);
    const [snack, setSnack] = useState({ open: false, severity: 'info', message: '' });

    const showSnack = (message, severity = 'error') =>
        setSnack({ open: true, severity, message });

    // Load firmware options for all types — fetch type is determined per MCU from coordinatorVersion
    useEffect(() => {
        setLoadingFirmware(true);
        const fetches = ALL_MCU_TYPES.map(mcuType => {
            const type = resolveFwType(mcuType, firmwareType);
            return ApiServices.searchFirmwareByMcuType(mcuType, type)
                .then(res => ({ mcuType, list: (res.status === 204 || !res.data) ? [] : res.data }))
                .catch(() => ({ mcuType, list: [] }));
        });
        Promise.all(fetches).then(results => {
            const opts = {};
            results.forEach(({ mcuType, list }) => { opts[mcuType] = list; });
            setFirmwareOptions(opts);
        }).finally(() => setLoadingFirmware(false));
    }, [firmwareType]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (ev) => {
            const lines = ev.target.result
                .split(/\r?\n/)
                .map(l => l.trim())
                .filter(Boolean);

            if (standalone) {
                // Line 1: gateway MAC (hex), rest: device MACs (hex)
                if (lines.length < 2) {
                    showSnack('File must contain gateway MAC on line 1 and at least one device MAC on subsequent lines.');
                    e.target.value = '';
                    return;
                }
                const macs = lines.slice(1).map(l => parseInt(l, 16));
                if (macs.some(isNaN)) {
                    showSnack('Device MACs (lines 2+) must be hex values (e.g. 00158D00019B35A7).');
                    e.target.value = '';
                    return;
                }
                setParsedGatewayMac(lines[0]);
                setDeviceMacs(macs);
            } else {
                // All lines are device MACs (gateway MAC comes from the gatewayId prop)
                if (lines.length === 0) {
                    showSnack('File is empty.');
                    e.target.value = '';
                    return;
                }
                const macs = lines.map(l => parseInt(l, 16));
                if (macs.some(isNaN)) {
                    showSnack('Device MACs must be hex values (e.g. 00158D00019B35A7), one per line.');
                    e.target.value = '';
                    return;
                }
                setDeviceMacs(macs);
            }
            setFileName(file.name);
        };
        reader.readAsText(file);
    };

    const handleToggleUpdateAll = (e) => {
        setUpdateAll(e.target.checked);
        if (e.target.checked) {
            setParsedGatewayMac('');
            setDeviceMacs([]);
            setFileName('');
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const resetForm = () => {
        setSelected(activeMcuTypes.reduce((acc, t) => ({ ...acc, [t]: '' }), {}));
        setStartTime('');
        setStopTime('');
        setUpdateAll(true);
        setParsedGatewayMac('');
        setDeviceMacs([]);
        setFileName('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!updateAll && deviceMacs.length === 0) {
            showSnack('Upload a file with device MACs to target specific devices.');
            return;
        }

        const binaryEntries = activeMcuTypes
            .filter(type => selected[type] !== '')
            .map(type => {
                const fw = (firmwareOptions[type] ?? []).find(f => f.version === selected[type]);
                if (!fw) return null;
                return {
                    type,
                    version: fw.version,
                    firmware_type: resolveFwType(type, firmwareType),
                };
            })
            .filter(Boolean);

        if (binaryEntries.length === 0) {
            showSnack('Select at least one firmware version.');
            return;
        }

        const imei = updateAll
            ? gatewayId
            : (standalone ? parsedGatewayMac
                          : gatewayId);

        const payload = {
            header: {
                timestamp: new Date().toISOString(),
                imei,
                id: `${imei}-${Date.now()}`,
            },
            binary: binaryEntries,
            update_all_devices: updateAll,
        };

        if (!updateAll) payload.devices = deviceMacs;
        payload.action = 1;
        if (startTime !== '') payload.start_time = timeToSeconds(startTime);
        if (stopTime !== '') payload.stop_time = timeToSeconds(stopTime);

        setSubmitting(true);
        ApiServices.startGatewayOta(payload)
            .then(response => {
                showSnack(`OTA started — session: ${response.data?.otaId ?? 'OK'}`, 'success');
                resetForm();
            })
            .catch(error => showSnack(error.response?.data ?? error.message))
            .finally(() => setSubmitting(false));
    };

    if (loadingFirmware) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    // File upload hint varies by context
    const fileHint = standalone
        ? <>Line 1: gateway MAC (hex)<br />Line 2+: device MACs (hex)</>
        : <>One device MAC per line<br />(hex, e.g. 00158D00019B35A7)</>;

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                autoHideDuration={4000}
                open={snack.open}
                onClose={() => setSnack(s => ({ ...s, open: false }))}
            >
                <Alert severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
                    {snack.message}
                </Alert>
            </Snackbar>

            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>

                <Box sx={{ flex: 1 }}>
                    {activeMcuTypes.map(type => {
                        const options = firmwareOptions[type] ?? [];
                        return (
                            <Box key={type} sx={ROW}>
                                <Typography variant="body2" sx={LABEL_SX}>{type}</Typography>
                                <TextField
                                    select
                                    size="small"
                                    sx={{ flex: 1 }}
                                    value={selected[type] ?? ''}
                                    onChange={e => setSelected(s => ({ ...s, [type]: e.target.value }))}
                                    disabled={options.length === 0}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {options.map(fw => (
                                        <MenuItem key={fw.version} value={fw.version}>
                                            {fw.version}{!fw.released ? ' (Not Released)' : ''}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                        );
                    })}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: 200 }}>
                    {!hideTimings && (
                        <>
                            <TimePicker24
                                label="Start Time"
                                value={startTime}
                                onChange={setStartTime}
                            />
                            <TimePicker24
                                label="Stop Time"
                                value={stopTime}
                                onChange={setStopTime}
                            />
                        </>
                    )}

                    <Divider sx={{ my: 0.5 }} />

                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={updateAll}
                                onChange={handleToggleUpdateAll}
                            />
                        }
                        label={<Typography variant="body2">Update All Devices</Typography>}
                        sx={{ mx: 0 }}
                    />

                    {!updateAll && (
                        <Box>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".txt,.csv"
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                            />
                            <Button
                                size="small"
                                variant="outlined"
                                fullWidth
                                startIcon={<UploadFile fontSize="small" />}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Upload Device List
                            </Button>
                            {fileName ? (
                                <Box sx={{ mt: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary" display="block" noWrap title={fileName}>
                                        {fileName}
                                    </Typography>
                                    {standalone && parsedGatewayMac && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Gateway: {parsedGatewayMac}
                                        </Typography>
                                    )}
                                    <Typography variant="caption" color="success.main" display="block">
                                        {deviceMacs.length} device{deviceMacs.length !== 1 ? 's' : ''} loaded
                                    </Typography>
                                </Box>
                            ) : (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    {fileHint}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button variant="contained" size="small" type="submit" disabled={submitting}>
                    {submitting ? 'Starting...' : 'Start OTA'}
                </Button>
            </Box>
        </Box>
    );
}

export default GatewayOtaForm;
